import { motion } from 'motion/react';
import { PropsWithChildren, Suspense, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Drawer } from '@/components/ui/daisy/drawer';
import { Loading } from '@/components/ui/daisy/loading';
import { fadeIn } from '@/utils/animations/commom';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';

type AppShellPartProps = PropsWithChildren;

type AppShellStyledPartProps = PropsWithChildren<{ className?: string }>;

function AppShellRoot({ children }: AppShellPartProps) {
  return (
    <Drawer openFrom="lg" className="relative h-screen w-screen bg-base-200">
      <motion.div
        className="pointer-events-none absolute inset-0 bg-base-100"
        {...fadeIn}
      />
      {children}
    </Drawer>
  );
}

function Sidebar({ children }: AppShellPartProps) {
  return (
    <Drawer.Side
      data-testid="appShellSidebar"
      className="lg:z-30 lg:overflow-visible"
    >
      {children}
    </Drawer.Side>
  );
}

function Body({ children }: AppShellPartProps) {
  return (
    <Drawer.Content className="relative flex min-h-0 flex-col">
      {children}
    </Drawer.Content>
  );
}

const Content = forwardRef<HTMLElement, AppShellStyledPartProps>(
  ({ className, children }, ref) => (
    <main
      ref={ref}
      className={twMerge(
        'relative flex flex-1 flex-col overflow-x-hidden overflow-y-auto rounded-xl bg-base-100',
        scrollBarYClassName,
        className,
      )}
    >
      <div className="mx-auto flex w-full max-w-[1440px] grow flex-col">
        {children}
      </div>
    </main>
  ),
);

Content.displayName = 'AppShell.Content';

function Header({ className, children }: AppShellStyledPartProps) {
  return (
    <div
      className={twMerge(
        'sticky top-0 z-30 px-4 before:absolute before:inset-0 before:-z-10 before:bg-base-100/30 before:backdrop-blur-[4px] lg:px-13 lg:pt-6 lg:before:bg-base-100/90',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Page({ children }: AppShellPartProps) {
  return (
    <div
      data-testid="appShellPage"
      className="flex grow flex-col gap-4 px-4 pt-4 pb-24 lg:px-13 lg:py-6"
    >
      <Suspense
        fallback={
          <div className="flex grow items-center justify-center">
            <Loading color="primary" size="lg" />
          </div>
        }
      >
        {children}
      </Suspense>
    </div>
  );
}

function Dock({ children }: AppShellPartProps) {
  return <div className="lg:hidden">{children}</div>;
}

const AppShell = Object.assign(AppShellRoot, {
  Sidebar,
  Body,
  Content,
  Header,
  Page,
  Dock,
});

export default AppShell;
